import { useState } from 'react';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { useSiteContent } from '@/context/SiteContent';

export function Contact() {
  const { contact } = useSiteContent();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post('/contact', form);
      toast.success('Perspective received. YUNTI will be in touch.');
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    'w-full px-4 py-3 bg-yunti-ground border border-yunti-ink/20 text-sm focus:border-yunti-purple focus:outline-none rounded-none';
  const labelCls = 'block text-xs font-mono uppercase tracking-wider text-yunti-ink/70 mb-1';

  return (
    <section id="contact" className="section-pad px-6 lg:px-12 bg-white text-yunti-ink border-b border-yunti-ink/10" data-testid="contact-section">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-8">
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-yunti-purple font-semibold block mb-2">
            {contact.eyebrow}
          </span>
          <h2 className="text-3xl font-display uppercase tracking-tight text-yunti-ink">{contact.title}</h2>
          <p className="text-sm text-yunti-ink/60 mt-2">{contact.support}</p>
          {contact.email && (
            <a
              href={`mailto:${contact.email}`}
              className="link-hover inline-block mt-2 text-xs font-mono uppercase tracking-widest text-yunti-green"
              data-testid="contact-email-link"
            >
              {contact.email}
            </a>
          )}
        </div>

        <form className="space-y-4" onSubmit={onSubmit} data-testid="contact-form">
          <div>
            <label className={labelCls} htmlFor="contact-name">
              Name
            </label>
            <input
              id="contact-name"
              type="text"
              required
              className={inputCls}
              placeholder="Your name"
              value={form.name}
              onChange={update('name')}
              data-testid="contact-name-input"
            />
          </div>
          <div>
            <label className={labelCls} htmlFor="contact-email">
              Email
            </label>
            <input
              id="contact-email"
              type="email"
              required
              className={inputCls}
              placeholder="you@domain.com"
              value={form.email}
              onChange={update('email')}
              data-testid="contact-email-input"
            />
          </div>
          <div>
            <label className={labelCls} htmlFor="contact-message">
              Perspective / Message
            </label>
            <textarea
              id="contact-message"
              rows="3"
              required
              className={inputCls}
              placeholder="Share your perspective..."
              value={form.message}
              onChange={update('message')}
              data-testid="contact-message-input"
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            className="w-full py-4 bg-yunti-ink text-white text-xs font-mono uppercase tracking-[0.2em] hover:bg-yunti-purple transition-colors rounded-none font-semibold disabled:opacity-60"
            data-testid="contact-submit-btn"
          >
            {sending ? 'Sending...' : 'Send Message'}
          </button>
        </form>
      </div>
    </section>
  );
}
